import {effect, inject, Injectable} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Meta, Title} from '@angular/platform-browser';
import {TranslateHelperService} from './translate-helper.service';

type MaybeArray<T> = T | T[];

export interface SeoConfig {
	titleKey?: MaybeArray<string>;
	title?: string;
	descriptionKey?: MaybeArray<string>;
	description?: string;
	imageUrl?: string;
	url?: string;
	path?: string;
	keywordsKey?: MaybeArray<string>;
	keywords?: string;
	hreflang?: string[];
}

/** Data for Article/BlogPosting JSON-LD structured data (single blog post view). */
export interface ArticleStructuredData {
	headline: string;
	description: string;
	path: string;
	imageUrl?: string;
	datePublished?: string;
	dateModified?: string;
}

@Injectable({
	            providedIn: 'root'
            })
export class SeoService {
	private readonly title = inject(Title);
	private readonly meta = inject(Meta);
	private readonly translateHelperService = inject(TranslateHelperService);
	private readonly document = inject(DOCUMENT, {optional: true});

	private readonly baseUrl = 'https://slaveous.com';
	private readonly defaultLang = 'en';
	private readonly defaultHreflang = ['fr', 'en', 'it', 'de', 'es'];
	private static readonly SCRIPT_ID_ARTICLE_SCHEMA = 'seo-article-schema';
	private readonly localeMap: Record<string, {languageName: string; ogLocale: string}> = {
		fr: {languageName: 'French', ogLocale: 'fr_FR'},
		en: {languageName: 'English', ogLocale: 'en_US'},
		it: {languageName: 'Italian', ogLocale: 'it_IT'},
		de: {languageName: 'German', ogLocale: 'de_DE'},
		es: {languageName: 'Spanish', ogLocale: 'es_ES'}
	};
	private readonly defaultConfig: Required<Pick<SeoConfig, 'titleKey' | 'descriptionKey' | 'imageUrl' | 'url'>> = {
		titleKey: 'MetaTitle.default',
		descriptionKey: 'MetaDescription.default',
		imageUrl: `${this.baseUrl}/assets/images/landing/Image_one_WEBP.webp`,
		url: `${this.baseUrl}/`
	};

	private currentConfig: SeoConfig = {};

	constructor() {
		effect(() => {
			void this.translateHelperService.language();
			this.applyConfig();
		});
	}

	update(config: SeoConfig): void {
		const normalized: SeoConfig = {...config};
		this.currentConfig = {...this.currentConfig, ...normalized};
		this.applyConfig();
	}

	reset(): void {
		this.currentConfig = {};
		this.clearArticleStructuredData();
		this.applyConfig();
	}

	/**
	 * Injects or updates Article/BlogPosting JSON-LD in the document head.
	 * Use one script tag with a fixed id so it can be replaced on route change (no duplicate or stale schema).
	 * Pass null to remove the article schema (e.g. when leaving the blog article page).
	 */
	setArticleStructuredData(data: ArticleStructuredData | null): void {
		if (!this.document) {
			return;
		}
		const scriptId = SeoService.SCRIPT_ID_ARTICLE_SCHEMA;
		const existingScript = this.document.getElementById(scriptId);
		if (existingScript) {
			existingScript.remove();
		}
		if (!data) {
			return;
		}
		const url = data.path ? this.normalizeUrl(data.path) : this.baseUrl;
		const imageUrl = data.imageUrl?.startsWith('/') ? `${this.baseUrl}${data.imageUrl}` : data.imageUrl;
		const schema: {
			'@context': string;
			'@type': string;
			headline: string;
			description: string;
			mainEntityOfPage: {'@type': string; '@id': string};
			author: {'@type': string; name: string; url: string};
			image?: string;
			datePublished?: string;
			dateModified?: string;
		} = {
			'@context': 'https://schema.org',
			'@type': 'BlogPosting',
			headline: data.headline,
			description: data.description,
			mainEntityOfPage: {
				'@type': 'WebPage',
				'@id': url
			},
			author: {
				'@type': 'Organization',
				name: 'Slaveous',
				url: this.baseUrl
			}
		};
		if (imageUrl) {
			schema['image'] = imageUrl;
		}
		if (data.datePublished) {
			schema['datePublished'] = data.datePublished;
		}
		if (data.dateModified) {
			schema['dateModified'] = data.dateModified;
		} else if (data.datePublished) {
			schema['dateModified'] = data.datePublished;
		}
		const script = this.document.createElement('script');
		script.type = 'application/ld+json';
		script.id = scriptId;
		script.textContent = JSON.stringify(schema);
		this.document.head.appendChild(script);
	}

	/** Removes the Article/BlogPosting JSON-LD script from the document head. */
	clearArticleStructuredData(): void {
		this.setArticleStructuredData(null);
	}

	private async applyConfig(): Promise<void> {
		const merged: SeoConfig = {...this.defaultConfig, ...this.currentConfig};
		const lang = this.translateHelperService.currentLang || this.defaultLang;
		this.applyDocumentLanguage(lang);

		const existingCanonical = this.document?.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? undefined;
		const localize = true;
		const canonicalUrl = this.currentConfig.url
			?? (merged.path ? this.normalizeUrl(merged.path, localize) : undefined)
			?? existingCanonical
			?? this.defaultConfig.url;
		this.meta.updateTag({property: 'og:url', content: canonicalUrl}, 'property="og:url"');

		if (this.document) {
			let canonicalLink = this.document.querySelector('link[rel="canonical"]');
			if (!canonicalLink) {
				canonicalLink = this.document.createElement('link');
				canonicalLink.setAttribute('rel', 'canonical');
				this.document.head.appendChild(canonicalLink);
			}
			canonicalLink.setAttribute('href', canonicalUrl);
		}

		this.updateHreflangLinks(merged.hreflang ?? this.defaultHreflang, canonicalUrl);
		this.applyLocaleMeta(lang);

		const title = await this.resolveTranslation(merged.titleKey, merged.title);
		if (title) {
			const formattedTitle = title.includes('Slaveous') ? title : `Slaveous - ${title}`;
			this.title.setTitle(formattedTitle);
			this.meta.updateTag({property: 'og:title', content: formattedTitle}, 'property="og:title"');
			this.meta.updateTag({name: 'twitter:title', content: formattedTitle}, 'name="twitter:title"');
		}
		this.updateBreadcrumbSchema(title, canonicalUrl, merged.path);

		const description = await this.resolveTranslation(merged.descriptionKey, merged.description);
		if (description) {
			this.meta.updateTag({name: 'description', content: description}, 'name="description"');
			this.meta.updateTag({property: 'og:description', content: description}, 'property="og:description"');
			this.meta.updateTag({name: 'twitter:description', content: description}, 'name="twitter:description"');
		}

		const rawImageUrl = merged.imageUrl ?? this.defaultConfig.imageUrl;
		const imageUrl = rawImageUrl?.startsWith('/') ? `${this.baseUrl}${rawImageUrl}` : rawImageUrl;
		// Update Open Graph image tags
		this.meta.updateTag({property: 'og:image', content: imageUrl}, 'property="og:image"');
		this.meta.updateTag({property: 'og:image:secure_url', content: imageUrl}, 'property="og:image:secure_url"');
		this.meta.updateTag({property: 'og:image:type', content: 'image/webp'}, 'property="og:image:type"');
		// Update Twitter image tags
		this.meta.updateTag({name: 'twitter:image', content: imageUrl}, 'name="twitter:image"');
		this.meta.updateTag({name: 'twitter:image:alt', content: 'Slaveous BDSM community'}, 'name="twitter:image:alt"');

		const keywords = merged.keywords ?? await this.resolveTranslation(merged.keywordsKey);
		if (keywords) {
			this.meta.updateTag({name: 'keywords', content: keywords}, 'name="keywords"');
		}
	}

	private updateBreadcrumbSchema(title: string | undefined, canonicalUrl: string, path?: string): void {
		if (!this.document) {
			return;
		}
		const scriptId = 'seo-breadcrumb-schema';
		const existingScript = this.document.getElementById(scriptId);
		const isHome = path === '/' || canonicalUrl === this.normalizeUrl('/');
		const items = [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Accueil',
				item: this.normalizeUrl('/')
			}
		];
		if (!isHome && title) {
			items.push({
				'@type': 'ListItem',
				position: 2,
				name: title.replace(/^Slaveous\s*-\s*/i, ''),
				item: canonicalUrl
			});
		}
		const breadcrumbSchema = {
			'@context': 'https://schema.org',
			'@type': 'BreadcrumbList',
			itemListElement: items
		};
		if (existingScript) {
			existingScript.textContent = JSON.stringify(breadcrumbSchema);
			return;
		}
		const script = this.document.createElement('script');
		script.type = 'application/ld+json';
		script.id = scriptId;
		script.textContent = JSON.stringify(breadcrumbSchema);
		this.document.head.appendChild(script);
	}

	private async resolveTranslation(keys?: MaybeArray<string>, fallback?: string): Promise<string | undefined> {
		if (fallback && fallback.trim().length > 0) {
			return fallback;
		}
		if (!keys) {
			return undefined;
		}
		const candidates = Array.isArray(keys) ? keys : [keys];
		for (const candidate of candidates) {
			const value = await this.translateHelperService.instant(candidate);
			if (value && value !== candidate) {
				return value;
			}
		}
		return undefined;
	}

	private normalizeUrl(path: string, localize = true): string {
		if (!path) {
			return this.defaultConfig.url;
		}
		const cleanedPath = path.startsWith('/') ? path : `/${path}`;
		if (!localize) {
			return `${this.baseUrl}${cleanedPath}`;
		}
		const lang = this.translateHelperService.currentLang || this.defaultLang;
		const hasLangPrefix = cleanedPath.split('/')[1] && this.defaultHreflang.includes(cleanedPath.split('/')[1]);
		const withLang = hasLangPrefix ? cleanedPath : `/${lang}${cleanedPath}`;
		return `${this.baseUrl}${withLang}`;
	}

	private updateHreflangLinks(langs: string[], href: string): void {
		if (!langs || langs.length === 0 || !this.document) {
			return;
		}
		const url = new URL(href);
		const pathSegments = url.pathname.split('/').filter(Boolean);
		const firstSegment = pathSegments[0];
		const hasLangPrefix = firstSegment && langs.includes(firstSegment);
		const pathWithoutLang = hasLangPrefix ? `/${pathSegments.slice(1).join('/')}` : url.pathname;
		for (const lang of langs) {
			const selector = `link[rel="alternate"][hreflang="${lang}"]`;
			let link = this.document.querySelector(selector);
			if (!link) {
				link = this.document.createElement('link');
				link.setAttribute('rel', 'alternate');
				link.setAttribute('hreflang', lang);
				this.document.head.appendChild(link);
			}
			const langPath = pathWithoutLang === '/' ? `/${lang}` : `/${lang}${pathWithoutLang}`;
			link.setAttribute('href', `${this.baseUrl}${langPath}`);
		}
		const xDefaultSelector = 'link[rel="alternate"][hreflang="x-default"]';
		let xDefault = this.document.querySelector(xDefaultSelector);
		if (!xDefault) {
			xDefault = this.document.createElement('link');
			xDefault.setAttribute('rel', 'alternate');
			xDefault.setAttribute('hreflang', 'x-default');
			this.document.head.appendChild(xDefault);
		}
		const xDefaultPath = pathWithoutLang === '/' ? `/${this.defaultLang}` : `/${this.defaultLang}${pathWithoutLang}`;
		xDefault.setAttribute('href', `${this.baseUrl}${xDefaultPath}`);
	}

	private applyDocumentLanguage(lang: string): void {
		if (this.document) {
			this.document.documentElement.lang = lang;
		}
	}

	private applyLocaleMeta(lang: string): void {
		const localeConfig = this.localeMap[lang];
		const ogLocale = localeConfig?.ogLocale ?? 'en_US';
		const languageName = localeConfig?.languageName ?? lang;
		this.meta.updateTag({property: 'og:locale', content: ogLocale}, 'property="og:locale"');
		this.meta.updateTag({name: 'language', content: languageName}, 'name="language"');

		if (!this.document) {
			return;
		}
		const alternates = this.defaultHreflang.filter((code) => code !== lang);
		const existingAlternates = Array.from(this.document.querySelectorAll('meta[property="og:locale:alternate"]'));
		existingAlternates.forEach((tag) => tag.parentNode?.removeChild(tag));
		for (const alt of alternates) {
			const altLocale = this.localeMap[alt]?.ogLocale;
			if (!altLocale) {
				continue;
			}
			const meta = this.document.createElement('meta');
			meta.setAttribute('property', 'og:locale:alternate');
			meta.setAttribute('content', altLocale);
			this.document.head.appendChild(meta);
		}
	}
}
